import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import Circular from 'components/common/CustomCircular';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { LearnAssignmentResult, listLearnAssignment } from 'stores/TrainingQuestionStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import LearnBreadcrumbs from './components/LearnBreadcrumbs';

const rate = (cleared: number, total: number) =>
  total === 0 ? 0 : Math.round((cleared / total) * 100);

/** 完了 = 全設問クリア（設問が1つ以上ある） */
const isCompleted = (a: LearnAssignmentResult) =>
  a.totalQuestionCount > 0 && a.clearedQuestionCount >= a.totalQuestionCount;

type FilterKey = 'all' | 'incomplete' | 'completed';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'incomplete', label: '未完了' },
  { key: 'completed', label: '完了' },
];

/** クエリの filter 値を正規化する（不正値は all）。 */
const normalizeFilter = (v: string | null): FilterKey =>
  v === 'incomplete' || v === 'completed' ? v : 'all';

/**
 * 割当課題一覧（受講・API6351）。
 * 自分に割り当てられた課題を進捗バー付きカードで表示する。
 * クエリ ?filter=all|incomplete|completed で簡易絞り込み（ダッシュボードからのディープリンク対応）。
 */
const AssignmentList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = normalizeFilter(searchParams.get('filter'));

  const [assignments, setAssignments] = React.useState<LearnAssignmentResult[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    listLearnAssignment()
      .then(setAssignments)
      .catch(() => errorDefault('課題の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  // 各フィルタの件数
  const counts = React.useMemo(() => {
    const completed = assignments.filter(isCompleted).length;
    return {
      all: assignments.length,
      completed,
      incomplete: assignments.length - completed,
    };
  }, [assignments]);

  const visible = React.useMemo(() => {
    if (filter === 'completed') return assignments.filter(isCompleted);
    if (filter === 'incomplete') return assignments.filter((a) => !isCompleted(a));
    return assignments;
  }, [assignments, filter]);

  const changeFilter = (next: FilterKey) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('filter');
        else p.set('filter', next);
        return p;
      },
      { replace: true },
    );
  };

  const emptyMessage =
    filter === 'completed'
      ? '完了した課題はありません'
      : filter === 'incomplete'
        ? '未完了の課題はありません'
        : '割り当てられている課題はありません';

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Circular open={loading} />
      <LearnBreadcrumbs items={[{ label: '研修', to: '/training-learn' }, { label: '課題' }]} />
      <Box sx={{ mb: 2.5 }}>
        <DomainHero
          eyebrow="Your Assignments"
          title="Assigned Challenges"
          tagline="Pick a challenge, open its questions, and solve them in the language of your choice. Your progress is tracked per language."
          palette={HERO_PALETTES.training}
          showHome={false}
        />
      </Box>

      {/* 簡易フィルタ（すべて / 未完了 / 完了） */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={(_, next) => next && changeFilter(next as FilterKey)}
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 1.75,
          },
        }}
      >
        {FILTERS.map((f) => (
          <ToggleButton key={f.key} value={f.key}>
            {f.label}
            <Box component="span" sx={{ ml: 0.75, color: 'text.secondary' }}>
              {counts[f.key]}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {!loading && visible.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {visible.map((a) => {
            const pct = rate(a.clearedQuestionCount, a.totalQuestionCount);
            const done = isCompleted(a);
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a.assignmentId}>
                <Paper
                  elevation={0}
                  onClick={() => navigate(`/training-learn/learn-assignment/${a.assignmentId}`)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3,
                    p: 2,
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { borderColor: '#00838f', transform: 'translateY(-2px)' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#00838f14',
                        color: '#00838f',
                      }}
                    >
                      <MenuBookIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {a.name}
                    </Typography>
                    {done && (
                      <Chip size="small" label="達成" color="success" sx={{ borderRadius: 1 }} />
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 40,
                    }}
                  >
                    {a.description}
                  </Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      {a.clearedQuestionCount} / {a.totalQuestionCount} 問クリア
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={done ? 'success' : 'primary'}
                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default AssignmentList;
